import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Grid } from '@mui/material';
import cx from 'classnames';

import { tt } from 'i18n/tt';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ThunkGetTicket, ThunkGetTicketNotes } from 'store/tickets/thunks';
import { ChatsSelector } from 'store/chats/selectors';
import {
  TicketFilterOptionsSelector,
  TicketNotesSelector,
  TicketSelector,
} from 'store/tickets/selectors';
import { addTicketChat } from 'store/tickets';
import { setActiveChat, setCurrentChatClosedStatus } from 'store/chats';

import Loader from 'components/Loader';
import CommonButton from 'components/Button/Button';
import Chip from 'pages/Teams/components/Chip';
import TicketTimeline from 'pages/Ticket/components/TicketTimeline/TicketTimeline';
import ChatBody from 'pages/Chats/components/ChatBody';
import CreateChatDialog from 'pages/Chats/dialogs/CreateChatDialog';
import SLABanner from 'components/SLABanner';
import { ToggleButtonGroup } from '../../../components/ToggleButtonGroup/ToggleButtonGroup';

import { ReactComponent as PulseIcon } from 'assets/pulse.icon.svg';
import { ReactComponent as TicketIcon } from 'assets/tickets.icon.svg';
import { ReactComponent as CompanyIcon } from 'assets/company.icon.svg';
import { ReactComponent as PersonIcon } from 'assets/user.icon.svg';
import { ReactComponent as ChatIcon } from 'assets/send.icon.svg';
import { ReactComponent as LockIcon } from 'assets/lock.icon.svg';

import NoNoteDoodle from 'assets/images/noNotes.png';
import StartChat from 'assets/images/startChat.png';

import useStyles from '../styles';
import { PortalConfigSelector } from 'store/config/selectors';
import { ThunkCloseChat, ThunkGetChats, ThunkReopenChat } from 'store/chats/thunks';
import EllipsisTooltip from '../../../components/EllipsisTooltip';
import { getFormattedDate } from '../../../utils/dates';
import { useSearchParams } from 'react-router-dom';

interface IProps {
  ticketId: string;
}

const TicketDetails: React.FC<IProps> = ({ ticketId }) => {
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(0);
  const [searchParams] = useSearchParams();
  const defaultButtonGroupValue =
    Object.fromEntries(searchParams.entries()).defaultButtonGroupValue || 'chat';
  const [buttonGroupValue, setButtonGroupValue] = useState(defaultButtonGroupValue);
  const showTimeline = buttonGroupValue === 'timeline';
  const [openCreateNewChatDialog, setOpenCreateNewChatDialog] = useState(false);
  const [newChatCreated, setNewChatCreated] = useState(false);

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const ticket = useAppSelector(TicketSelector);
  const notes = useAppSelector(TicketNotesSelector);
  const chats = useAppSelector(ChatsSelector);
  const { closeStatusId } = useAppSelector(TicketFilterOptionsSelector);

  const config = useAppSelector(PortalConfigSelector);
  const ticketChats = useMemo(
    () => chats.filter(i => i.ticketId.toString() === ticket?.id?.toString()),
    [chats, ticket],
  );
  const isAllClosedChats = useMemo(
    () => !ticketChats.filter(i => !i.isClosed).length,
    [ticketChats],
  );

  const handleResolve = (): void => {
    setLoading(true);
    dispatch(ThunkCloseChat({ chatId: selectedChat }))
      .unwrap()
      .then(() => {
        dispatch(setCurrentChatClosedStatus({ chatId: selectedChat, status: true }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReopen = (): void => {
    setLoading(true);
    dispatch(ThunkReopenChat({ chatId: selectedChat }))
      .unwrap()
      .then(() => {
        dispatch(setCurrentChatClosedStatus({ chatId: selectedChat, status: false }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isTicketClosed = useMemo(() => {
    return Boolean(ticket?.statusId && +ticket.statusId === +closeStatusId);
  }, [ticket?.statusId, closeStatusId]);

  const timeline = useMemo(() => {
    if (notes.length === 0) {
      return (
        <div className={classes.noNotes}>
          <img src={NoNoteDoodle} alt="No notes" />
          {tt('pages.teams.ticketDetails.noNotesYet')}
        </div>
      );
    }

    return <TicketTimeline hideNoteInput />;
  }, [notes]);

  const chat = useMemo(() => {
    if (!ticketChats.length) {
      return (
        <div className={classes.noNotes}>
          <img src={StartChat} alt="Start Chat" />
          {isTicketClosed ? (
            <div className={classes.startChat}>{tt('pages.teams.ticketDetails.ticketClosed')}</div>
          ) : (
            <div className={classes.startChat}>
              {tt('pages.teams.ticketDetails.noChatYet')}
              <Button
                onClick={() => {
                  setOpenCreateNewChatDialog(true);
                }}
                variant="outlined"
                endIcon={<ChatIcon />}
                className={classes.startChatButton}
              >
                {tt('pages.teams.ticketDetails.startChat')}
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (selectedChat > 0) {
      return (
        <div className={classes.chatContainer}>
          <ChatBody activeChatId={selectedChat} isTicketClosed={isTicketClosed} />
        </div>
      );
    }

    if (ticketChats.length) {
      return (
        <div className={classes.chatList}>
          {isAllClosedChats && (
            <Grid display="flex" justifyContent="flex-end">
              <Button
                onClick={() => {
                  setOpenCreateNewChatDialog(true);
                }}
                variant="contained"
                endIcon={<ChatIcon />}
                className={classes.startButton}
              >
                {tt('pages.teams.ticketDetails.startChat')}
              </Button>
            </Grid>
          )}
          {ticketChats.map(i => (
            <div
              className={cx(classes.chatListItem, { [classes.chatListItemClosed]: i.isClosed })}
              key={i.id}
              onClick={() => {
                setSelectedChat(i.id);
              }}
            >
              <Grid display="flex">
                <div className={classes.chatLockIconContainer}>
                  {i.isClosed && <LockIcon className={classes.chatLockIcon} />}
                </div>
                <div>{i.name}</div>
              </Grid>
              {i.createdOn && <div>created on: {getFormattedDate(i.createdOn)}</div>}
            </div>
          ))}
        </div>
      );
    }
  }, [ticket?.id, selectedChat, newChatCreated, classes, chats]);

  const isChatClosed = useMemo(
    () => chats.find(i => i.id === selectedChat)?.isClosed,
    [chats, selectedChat],
  );

  const onMount = useCallback(() => {
    setLoading(true);
    dispatch(ThunkGetChats({ search: '' }));

    if (!ticketId) return;

    Promise.all([
      dispatch(ThunkGetTicketNotes({ ticketId })),
      dispatch(ThunkGetTicket({ ticketId })),
    ]).then(() => {
      setLoading(false);
    });

    setNewChatCreated(false);
  }, [ticketId]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  useEffect(() => {
    if (ticket?.chatId) {
      dispatch(setActiveChat(ticket.chatId));
    }
  }, [ticket]);

  useEffect(() => {
    const activeChat = ticketChats.find(i => !i.isClosed);
    if (activeChat) {
      setSelectedChat(activeChat.id);
    }
    return () => {
      setSelectedChat(0);
    };
  }, [ticketChats]);

  useEffect(() => {
    if (config.disableChats && buttonGroupValue === 'chat') {
      setButtonGroupValue('timeline');
    }
  }, [config.disableChats, buttonGroupValue]);

  if (loading) {
    return (
      <div className={classes.ticketDetails}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.ticketDetailsContainer}>
      <Grid display="flex" justifyContent="space-between" width="100%">
        <div className={classes.ticketInfoTitle}>
          <EllipsisTooltip text={ticket?.title ?? ''} typographyProps={{ variant: 'h5' }} />
        </div>
        {ticketChats.length > 1 && !!selectedChat && (
          <CommonButton
            text={tt('buttons.back')}
            onClick={() => {
              setSelectedChat(0);
            }}
          />
        )}
      </Grid>

      <SLABanner />
      <div className={classes.ticketInfo}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Chip icon={<TicketIcon />} title={`#${ticket?.ticketNumber ?? ticket?.id ?? ''}`} />
          <Chip icon={<PulseIcon />} title={ticket?.status ?? ''} />
          <Chip icon={<CompanyIcon />} title={ticket?.companyName ?? ticket?.company ?? ''} />
          <Chip icon={<PersonIcon />} title={ticket?.contactName ?? ticket?.contact ?? ''} />
        </div>

        {!!selectedChat && (
          <div>
            {!isChatClosed ? (
              <CommonButton text={tt('pages.chats.resolveButton')} onClick={handleResolve} />
            ) : (
              config.allowReOpenChat && (
                <CommonButton text={tt('pages.chats.reopenButton')} onClick={handleReopen} />
              )
            )}
          </div>
        )}

        <div className={classes.multiSwitch}>
          <ToggleButtonGroup
            onChange={setButtonGroupValue}
            value={buttonGroupValue}
            buttons={[
              { label: tt('pages.teams.ticketDetails.timeline'), value: 'timeline' },
              ...(config.disableChats
                ? []
                : [
                    {
                      label: tt('pages.teams.ticketDetails.chat'),
                      value: 'chat',
                    },
                  ]),
            ]}
          />
        </div>
      </div>

      <div className={classes.timelineContainer}>{showTimeline ? timeline : chat}</div>

      {openCreateNewChatDialog && (
        <CreateChatDialog
          open={openCreateNewChatDialog}
          onClose={() => {
            setOpenCreateNewChatDialog(false);
          }}
          title={tt('pages.teams.ticketDetails.startChatTitle')}
          ticketId={ticket?.id}
          ticketNumber={ticket?.ticketNumber}
          onSubmit={chatId => {
            if (ticket?.id) {
              dispatch(addTicketChat({ ticketId: ticket?.id, chatId }));
              dispatch(setActiveChat(chatId));
              setNewChatCreated(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default TicketDetails;
